// apps/admin-ui/src/pages/DataTablePage.tsx
// Generic drill-down table page using config
// ALL RIGHTS RESERVED
//
// FIXED 2026-02-01: Removed require() usage in getMockData (not valid in Vite/ESM)
// Now uses location.pathname directly instead of looking up from config
// UPDATED 2026-02-01: Added RBAC permission checking for action buttons
// UPDATED 2026-02-01: Added action handlers for approve/reject operations
// UPDATED 2026-02-04: Added array handling in formatCellValue (renders as wrapped tags)

import { useState, useEffect, useCallback } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { getTableConfig, type TableConfig, type ColumnDef, type ActionDef } from '../config/tableConfigs';
import { api } from '../api/client';
import { getCurrentEmployee } from '../utils/auth';
import { hasPermission, type Permission } from '../utils/rbac';
import '../styles/data-table.css';

// =============================================================================
// TYPES
// =============================================================================

interface SortState {
  key: string;
  direction: 'asc' | 'desc';
}

interface FilterState {
  [key: string]: string;
}

// =============================================================================
// HELPERS
// =============================================================================

function formatCellValue(value: any, column: ColumnDef): React.ReactNode {
  if (value === null || value === undefined) {
    return <span className="cell-empty">—</span>;
  }

  // Use custom renderer if provided
  if (column.render) {
    return column.render(value, {});
  }

  // Handle arrays (e.g., contractsChanged)
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return <span className="cell-empty">—</span>;
    }
    
    // Special handling for contract names with status
    const isContractColumn = column.key === 'contractsChanged' || column.key === 'contracts';
    
    // Sort items alphabetically by name
    const sorted = [...value].sort((a, b) => {
      const nameA = (typeof a === 'string' ? a : a?.name || '').toLowerCase();
      const nameB = (typeof b === 'string' ? b : b?.name || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
    
    return (
      <span className="cell-array">
        {sorted.map((item, idx) => {
          // Extract name and status - handle both object and string formats
          let name = '';
          let status = 'pending';
          let notes = '';
          
          if (typeof item === 'string') {
            name = item;
          } else if (item !== null && item !== undefined) {
            // It's an object - safely extract properties
            name = String(item.name || '');
            status = String(item.status || 'pending');
            notes = String(item.notes || '');
          }
          
          // Skip if no valid name
          if (!name || name === 'undefined' || name === 'null') {
            return null;
          }
          
          const statusClass = `status-${status.replace(/_/g, '-')}`;
          const title = notes ? `${status}: ${notes}` : status;
          
          if (isContractColumn) {
            return (
              <a
                key={idx}
                href={`/contracts?contract=${encodeURIComponent(name)}`}
                className={`contract-tag ${statusClass}`}
                title={title}
              >
                {name}
              </a>
            );
          }
          
          return <span key={idx} className="array-tag">{name}</span>;
        })}
      </span>
    );
  }

  // Default formatting based on column key hints
  if (column.key.includes('At') || column.key.includes('date') || column.key.includes('Date')) {
    try {
      return new Date(value).toLocaleString();
    } catch {
      return value;
    }
  }

  if (column.key.includes('amount') || column.key.includes('Amount') || column.key.includes('fee') || column.key.includes('Fee')) {
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(num);
  }

  // Status badges
  if (column.key === 'status' || column.key === 'severity') {
    const statusClass = `status-${String(value).toLowerCase().replace(/_/g, '-')}`;
    return <span className={`status-badge ${statusClass}`}>{value}</span>;
  }

  return String(value);
}

/**
 * Check if user has permission for an action
 */
function canPerformAction(action: ActionDef): boolean {
  // If action is explicitly disabled in config, respect that
  if (!action.enabled) {
    return false;
  }

  // Check RBAC permission
  const employee = getCurrentEmployee();
  if (!employee) {
    return false;
  }

  return hasPermission(employee.roles, action.permission as Permission);
}

// =============================================================================
// LOADING SKELETON
// =============================================================================

function TableSkeleton({ columns }: { columns: ColumnDef[] }) {
  return (
    <table className="data-table skeleton">
      <thead>
        <tr>
          {columns.map(col => (
            <th key={col.key} style={{ width: col.width }}>{col.label}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {[...Array(5)].map((_, i) => (
          <tr key={i}>
            {columns.map(col => (
              <td key={col.key}>
                <div className="skeleton-cell" />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// =============================================================================
// MOCK DATA FOR DEVELOPMENT
// =============================================================================

/**
 * Generate mock data based on the current route path
 * FIXED: Uses path parameter instead of require() to look up config
 */
function getMockData(path: string): any[] {
  if (path.includes('kyc')) {
    return [
      { id: 'KYC-001', email: 'alice@example.com', type: 'individual', status: 'pending', submittedAt: new Date().toISOString(), assignedTo: 'John D.' },
      { id: 'KYC-002', email: 'bob@example.com', type: 'business', status: 'in_review', submittedAt: new Date().toISOString(), assignedTo: 'Jane S.' },
      { id: 'KYC-003', email: 'carol@example.com', type: 'individual', status: 'pending', submittedAt: new Date().toISOString(), assignedTo: null },
    ];
  }

  if (path.includes('aml')) {
    return [
      { id: 'AML-001', userId: 'USR-123', type: 'Unusual Activity', severity: 'high', status: 'open', createdAt: new Date().toISOString(), description: 'Multiple large transfers in short period' },
      { id: 'AML-002', userId: 'USR-456', type: 'Velocity Check', severity: 'medium', status: 'investigating', createdAt: new Date().toISOString(), description: 'Exceeded daily transfer limit' },
    ];
  }

  if (path.includes('mints')) {
    return [
      { id: 'MINT-001', userId: 'USR-789', amount: 5000, status: 'pending', createdAt: new Date().toISOString(), source: 'Bank Transfer' },
      { id: 'MINT-002', userId: 'USR-012', amount: 10000, status: 'pending', createdAt: new Date().toISOString(), source: 'Wire' },
      { id: 'MINT-003', userId: 'USR-345', amount: 2500, status: 'approved', createdAt: new Date().toISOString(), source: 'Bank Transfer' },
    ];
  }

  if (path.includes('withdrawals')) {
    return [
      { id: 'WD-001', userId: 'USR-ALICE', userEmail: 'alice@example.com', amount: 25, fee: 0.13, status: 'pending', createdAt: new Date().toISOString(), destination: 'Bank (ACH)' },
      { id: 'WD-002', userId: 'USR-BOB', userEmail: 'bob@example.com', amount: 500, fee: 2.50, status: 'pending', createdAt: new Date().toISOString(), destination: 'Bank (Dwolla)' },
    ];
  }

  if (path.includes('transactions')) {
    return [
      { id: 'TX-001', type: 'mint', fromUserId: null, toUserId: 'USR-789', amount: 5000, fee: 25, status: 'completed', createdAt: new Date().toISOString() },
      { id: 'TX-002', type: 'transfer', fromUserId: 'USR-789', toUserId: 'USR-012', amount: 1000, fee: 5, status: 'completed', createdAt: new Date().toISOString() },
      { id: 'TX-003', type: 'burn', fromUserId: 'USR-345', toUserId: null, amount: 2500, fee: 12.5, status: 'pending', createdAt: new Date().toISOString() },
    ];
  }

  if (path.includes('vulns') || path.includes('vulnerabilities')) {
    return [
      { id: 'VULN-001', title: 'SQL Injection in user search', severity: 'critical', status: 'open', assignedTo: 'Security Team', reportedAt: new Date().toISOString() },
      { id: 'VULN-002', title: 'XSS in admin comments', severity: 'high', status: 'in_progress', assignedTo: 'John D.', reportedAt: new Date().toISOString() },
    ];
  }

  if (path.includes('security/alerts')) {
    return [
      { id: 'ALERT-001', type: 'Failed Login', severity: 'medium', status: 'open', createdAt: new Date().toISOString(), description: 'Multiple failed login attempts from IP 192.168.1.100' },
    ];
  }

  if (path.includes('contracts/reviews')) {
    return [
      { id: 'REV-001', contractName: 'FeeContract', version: '2.5.0', status: 'pending', submittedAt: new Date().toISOString(), reviewer: null },
      { id: 'REV-002', contractName: 'MintContract', version: '1.2.1', status: 'in_review', submittedAt: new Date().toISOString(), reviewer: 'Alice A.' },
    ];
  }

  if (path.includes('contracts')) {
    return [
      { id: 'DEP-001', contractName: 'FeeContract', version: '2.4.1', network: 'ethereum', address: '0x1234...abcd', deployedAt: new Date().toISOString(), deployedBy: 'DevOps' },
      { id: 'DEP-002', contractName: 'TransferContract', version: '3.0.0', network: 'polygon', address: '0x5678...efgh', deployedAt: new Date().toISOString(), deployedBy: 'DevOps' },
    ];
  }

  if (path.includes('governance')) {
    return [
      { id: 'PROP-001', title: 'Increase reserve ratio to 2.5:1', type: 'parameter', status: 'active', votesFor: 5, votesAgainst: 2, deadline: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString() },
    ];
  }

  if (path.includes('audit/reports')) {
    return [
      { id: 'AUD-001', title: 'Q4 2025 Financial Audit', type: 'financial', status: 'completed', auditor: 'Deloitte', completedAt: new Date().toISOString() },
      { id: 'AUD-002', title: 'Smart Contract Security Audit', type: 'security', status: 'in_progress', auditor: 'Trail of Bits', completedAt: null },
    ];
  }

  if (path.includes('audit/attestation')) {
    return [
      { id: 'ATT-001', month: 'January 2026', status: 'completed', attestedBy: 'CPA Firm', attestedAt: new Date().toISOString() },
    ];
  }

  if (path.includes('custody/holdings')) {
    return [
      { id: 'HOLD-001', assetType: 'USD Cash', amount: 50000000, custodian: 'Bank of America', verifiedAt: new Date().toISOString() },
      { id: 'HOLD-002', assetType: 'T-Bills', amount: 30000000, custodian: 'Fidelity', verifiedAt: new Date().toISOString() },
    ];
  }

  // Generic mock data
  return [
    { id: 'MOCK-001', status: 'pending', createdAt: new Date().toISOString() },
    { id: 'MOCK-002', status: 'completed', createdAt: new Date().toISOString() },
  ];
}

// =============================================================================
// ACTION HANDLERS
// =============================================================================

/**
 * Get the API endpoint for an action
 */
function getActionEndpoint(config: TableConfig, actionId: string, rowId: string): string {
  // Map table endpoints to action endpoints
  const endpointBase = config.endpoint.replace('/pending', '').replace('/queue', '');
  return `${endpointBase}/${rowId}/${actionId}`;
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function DataTablePage() {
  const location = useLocation();
  const config = getTableConfig(location.pathname);

  const [data, setData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sort, setSort] = useState<SortState | null>(null);
  const [filters, setFilters] = useState<FilterState>({});
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  // Initialize sort from config
  useEffect(() => {
    if (config?.defaultSort) {
      setSort(config.defaultSort);
    }
  }, [config]);

  // Fetch data function (memoized for refresh capability)
  const fetchData = useCallback(async () => {
    if (!config) return;

    setIsLoading(true);
    setError(null);

    try {
      // Build query params
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('pageSize', String(config.pageSize || 20));
      
      if (sort) {
        params.set('sortBy', sort.key);
        params.set('sortDir', sort.direction);
      }

      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.set(key, value);
      });

      const response = await api.get(`${config.endpoint}?${params.toString()}`);
      
      setData(response.data || response.items || []);
      setTotalPages(response.totalPages || 1);
    } catch (err: any) {
      setError(err.message || 'Failed to load data');
      // Use mock data for development - pass location.pathname instead of config
      setData(getMockData(location.pathname));
    } finally {
      setIsLoading(false);
    }
  }, [config, page, sort, filters, location.pathname]);

  // Fetch data on mount and when dependencies change
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle column sort
  const handleSort = (column: ColumnDef) => {
    if (!column.sortable) return;

    setSort(prev => {
      if (prev?.key === column.key) {
        return { key: column.key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key: column.key, direction: 'asc' };
    });
  };

  // Handle filter change
  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1);
  };

  // Handle action button click
  const handleAction = async (action: ActionDef, row: any) => {
    if (!config) return;

    const confirmMessage = action.id === 'approve' 
      ? `Are you sure you want to approve this ${config.title.replace(' Requests', '').replace(' Queue', '').toLowerCase()}?`
      : action.id === 'reject'
      ? `Are you sure you want to reject this ${config.title.replace(' Requests', '').replace(' Queue', '').toLowerCase()}?`
      : `Are you sure you want to ${action.label.toLowerCase()} this item?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setActionLoading(`${action.id}-${row.id}`);
    setActionError(null);

    try {
      const endpoint = getActionEndpoint(config, action.id, row.id);
      
      // Include employee identity headers so the API can record who performed the action
      const employee = getCurrentEmployee();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer admin_test',
      };
      if (employee) {
        headers['x-dev-employee-id'] = String(employee.id || '');
        headers['x-dev-employee-name'] = employee.name || employee.email || `Employee ${employee.id}`;
        headers['x-dev-employee-role'] = (employee.roles && employee.roles[0]) || 'admin';
      }

      const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3030';
      const res = await fetch(`${API_BASE}${endpoint}`, {
        method: 'POST',
        headers,
        body: JSON.stringify({}),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({ message: 'Request failed' }));
        throw new Error(errData.message || `HTTP ${res.status}`);
      }
      
      // Refresh the data after successful action
      await fetchData();
    } catch (err: any) {
      setActionError(err.message || `Failed to ${action.label.toLowerCase()}`);
      // Clear error after 5 seconds
      setTimeout(() => setActionError(null), 5000);
    } finally {
      setActionLoading(null);
    }
  };

  // Config not found
  if (!config) {
    return (
      <div className="data-table-page error">
        <h2>Page Not Found</h2>
        <p>No configuration found for this page.</p>
        <Link to="/">Return to Dashboard</Link>
      </div>
    );
  }

  return (
    <div className="data-table-page">
      {/* Header */}
      <header className="table-header">
        <div className="header-left">
          <Link to="/" className="back-link">← Dashboard</Link>
          <h1>{config.title}</h1>
        </div>
        <div className="header-right">
          <button className="refresh-btn" onClick={fetchData} disabled={isLoading}>
            {isLoading ? 'Loading...' : 'Refresh'}
          </button>
          <button className="export-btn" disabled>
            Export CSV
          </button>
        </div>
      </header>

      {/* Action Error */}
      {actionError && (
        <div className="action-error">
          <p>⚠️ {actionError}</p>
        </div>
      )}

      {/* Filters */}
      {config.filters && config.filters.length > 0 && (
        <div className="table-filters">
          {config.filters.map(filter => (
            <div key={filter.key} className="filter-item">
              <label htmlFor={filter.key}>{filter.label}</label>
              {filter.type === 'select' && filter.options && (
                <select
                  id={filter.key}
                  value={filters[filter.key] || ''}
                  onChange={e => handleFilterChange(filter.key, e.target.value)}
                >
                  {filter.options.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              )}
              {filter.type === 'text' && (
                <input
                  id={filter.key}
                  type="text"
                  value={filters[filter.key] || ''}
                  onChange={e => handleFilterChange(filter.key, e.target.value)}
                  placeholder={`Filter by ${filter.label.toLowerCase()}`}
                />
              )}
            </div>
          ))}
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="table-error">
          <p>{error}</p>
          <small>Showing mock data for development</small>
        </div>
      )}

      {/* Table */}
      <div className="table-container">
        {isLoading ? (
          <TableSkeleton columns={config.columns} />
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                {config.columns.map(col => (
                  <th
                    key={col.key}
                    style={{ width: col.width, textAlign: col.align }}
                    className={col.sortable ? 'sortable' : ''}
                    onClick={() => handleSort(col)}
                  >
                    {col.label}
                    {sort?.key === col.key && (
                      <span className="sort-indicator">
                        {sort.direction === 'asc' ? ' ↑' : ' ↓'}
                      </span>
                    )}
                  </th>
                ))}
                {config.actions && config.actions.length > 0 && (
                  <th style={{ width: '160px' }}>Actions</th>
                )}
              </tr>
            </thead>
            <tbody>
              {data.length === 0 ? (
                <tr>
                  <td colSpan={config.columns.length + (config.actions?.length ? 1 : 0)} className="empty-row">
                    No data found
                  </td>
                </tr>
              ) : (
                data.map((row, index) => (
                  <tr key={row.id || index}>
                    {config.columns.map(col => (
                      <td key={col.key} style={{ textAlign: col.align }}>
                        {formatCellValue(row[col.key], col)}
                      </td>
                    ))}
                    {config.actions && config.actions.length > 0 && (
                      <td className="actions-cell">
                        {config.actions.map(action => {
                          const isAllowed = canPerformAction(action);
                          const isLoadingThis = actionLoading === `${action.id}-${row.id}`;
                          
                          // Action-specific status gating:
                          // - "claim" is available when pending
                          // - "approve"/"reject" are available when pending or in_review
                          // - All other actions: available when pending
                          const status = row.status;
                          const isActionable = 
                            action.id === 'claim' ? status === 'pending' :
                            action.id === 'approve' || action.id === 'reject' 
                              ? (status === 'pending' || status === 'in_review') :
                            status === 'pending';
                          
                          return (
                            <button
                              key={action.id}
                              className={`action-btn action-btn--${action.variant || 'secondary'}`}
                              disabled={!isAllowed || isLoadingThis || !isActionable}
                              title={
                                !action.enabled ? 'Coming soon' :
                                !isAllowed ? 'You do not have permission' :
                                !isActionable ? `Cannot ${action.label.toLowerCase()} (status: ${status})` :
                                action.label
                              }
                              onClick={() => handleAction(action, row)}
                            >
                              {isLoadingThis ? '...' : action.label}
                            </button>
                          );
                        })}
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="table-pagination">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            Previous
          </button>
          <span className="page-info">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

export default DataTablePage;
